import * as Sentry from '@sentry/react';
import { createBrowserHistory } from 'history';

import { STORAGE_KEYS } from 'constants/enums';

import { isRNWebview, RELEASE_VERSION, SENTRY_DSN } from './constants/constants';

const history = createBrowserHistory();

const isStatus4xx = (hint: any) => {
  const status = hint?.originalException?.status;
  return typeof status === 'string' && /^4\d{2}$/.test(status);
};

const ignoreExceptionToSentry = (exception: string) => {
  const ignoreErrorPatterns = [
    'Request has been terminated',
    'internal_server_error',
    'You provided an invalid object where a stream was expected',
    'Non-Error exception captured with keys: currentTarget, isTrusted',
    'Load failed',
    'Non-Error exception captured with keys: code, message',
    'Loading chunk',
    'Loading CSS chunk',
    "Cannot read properties of null (reading 'style')",
    "Failed to execute 'scrollTo' on 'Window'",
    'ResizeObserver loop completed with undelivered notifications.',
    'window.onMetaWidget is not a constructor',
    'Failed to fetch',
    'HTTP error',
    'o is not a function',
    'Unexpected token "<"',
    'Navigation cancelled',
    'AbortError',
    'The operation was aborted',
    'Network request failed',
    'Request aborted',
    'Request failed with status code 499', // Client Closed Request
    'Request timed out',
    'User aborted',
    'Error: Request has been timed out',
  ];
  return ignoreErrorPatterns.filter(err => exception.indexOf(err) !== -1).length > 0;
};

export const initSentry = () => {
  if (SENTRY_DSN) {
    Sentry.init({
      dsn: SENTRY_DSN,
      integrations: [
        Sentry.reactRouterV5BrowserTracingIntegration({ history }),
        Sentry.extraErrorDataIntegration(),
        Sentry.httpClientIntegration(),
        // Add browser profiling integration to the list of integrations
        Sentry.browserTracingIntegration(),
        Sentry.browserProfilingIntegration(),
      ],
      release: RELEASE_VERSION,
      beforeSend(event, hint) {
        // Check if it's a navigation-related error
        const error = hint?.originalException;
        if (error instanceof Error) {
          // Filter out aborted requests and navigation errors
          if (
            error.name === 'AbortError' ||
            error.message.includes('canceled') ||
            error.message.includes('aborted')
          ) {
            return null;
          }
        }

        // Check for 4xx errors and ignored patterns
        return isStatus4xx(hint) ||
          ignoreExceptionToSentry(hint?.originalException?.toString() || '')
          ? null
          : event;
      },
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      tracesSampleRate: 0.01,
      profilesSampleRate: 1.0,
      normalizeDepth: 5,
      sendDefaultPii: true,
      environment: process.env.REACT_APP_ENV,
      enabled:
        process.env.REACT_APP_ENV === 'production' ||
        process.env.REACT_APP_ENV === 'beta',
    });

    const persistRoot = localStorage.getItem(STORAGE_KEYS.PERSIST_ROOT) || '{}';
    const localUserObjectStr = JSON.parse(persistRoot)?.user || '{}';

    const localUserObject = JSON.parse(localUserObjectStr);

    const userContext = localUserObject?.id
      ? {
          id: localUserObject.id,
          username: localUserObject.userName,
          email: localUserObject.email,
          source: isRNWebview() ? 'ReactNative' : 'WebApp',
          userAuthStatus: 'logged-in',
        }
      : {
          source: isRNWebview() ? 'ReactNative' : 'WebApp',
          userAuthStatus: 'logged-out',
          environment: process.env.REACT_APP_ENV,
        };
    Sentry.setUser(userContext);
  }
};

export default Sentry;
